import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import BusinessIcon from "@material-ui/icons/Business";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import NotificationSoundSettingsModal from "../components/NotificationSoundSettingsModal";
import OldTicketsReminderModal from "../components/OldTicketsReminderModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";

const drawerWidth = 240;

// Fecha de la versión visual instalada (AAAA.MM.DD).
const APP_VERSION = "2026.10.22";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    minHeight: "48px",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.background.default,
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
    color: theme.palette.text.primary,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
  },
  drawerFooter: {
    marginTop: "auto",
    padding: "10px 8px",
    borderTop: "1px solid rgba(0,0,0,0.08)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  versionCard: {
    width: "100%",
    padding: "10px 11px",
    borderRadius: 10,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(144, 202, 249, 0.24)"
        : "rgba(37, 118, 210, 0.18)"
    }`,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(37, 118, 210, 0.20), rgba(37, 118, 210, 0.06))"
        : "linear-gradient(135deg, rgba(37, 118, 210, 0.10), rgba(255, 255, 255, 0.96))",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 4px 14px rgba(0, 0, 0, 0.18)"
        : "0 4px 14px rgba(37, 118, 210, 0.08)",
  },

  versionBrandRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  versionIcon: {
    width: 28,
    height: 28,
    padding: 6,
    borderRadius: 8,
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(144, 202, 249, 0.14)"
        : "rgba(37, 118, 210, 0.10)",
  },

  versionCopy: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },

  versionEyebrow: {
    fontSize: 8,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },

  versionBrand: {
    marginTop: 1,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.25,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
  },

  versionMeta: {
    marginTop: 7,
    paddingTop: 6,
    borderTop: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.10)"
        : "rgba(37, 118, 210, 0.12)"
    }`,
    fontSize: 9,
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: "0.25px",
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },

  versionCompact: {
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(144, 202, 249, 0.14)"
        : "rgba(37, 118, 210, 0.10)",
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.background.paper,
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.8)",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
  },
  themeIcon: {
    color: theme.palette.text.primary,
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleOpenNotificationSettings = () => {
    setNotificationSettingsOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>
          <MainListItems drawerClose={drawerClose} />
        </List>
        <Divider />
        <div className={classes.drawerFooter}>
          {drawerOpen ? (
            <div className={classes.versionCard}>
              <div className={classes.versionBrandRow}>
                <BusinessIcon className={classes.versionIcon} />
                <div className={classes.versionCopy}>
                  <span className={classes.versionEyebrow}>
                    Sistema de comunicaciones
                  </span>
                  <span className={classes.versionBrand}>by SINDEN S.A.S.</span>
                </div>
              </div>
              <div className={classes.versionMeta}>
                Versión {APP_VERSION}
              </div>
            </div>
          ) : (
            <div
              className={classes.versionCompact}
              title={`SINDEN S.A.S. · Versión ${APP_VERSION}`}
            >
              <BusinessIcon fontSize="small" />
            </div>
          )}
        </div>
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <NotificationSoundSettingsModal
        open={notificationSettingsOpen}
        onClose={() => setNotificationSettingsOpen(false)}
      />
      <OldTicketsReminderModal />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={classes.title}
          >
            WhaTicket
          </Typography>

          <div className={classes.themeSwitchContainer}>
            <Brightness4Icon className={classes.themeIcon} />
            <Switch
              checked={darkMode}
              onChange={toggleTheme}
              color="default"
              className={classes.switch}
            />
          </div>

          {user.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleOpenNotificationSettings}>
                <NotificationsActiveIcon fontSize="small" style={{ marginRight: 8 }} />
                {i18n.t("mainDrawer.appBar.user.notifications")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
